import { BrandConfig, DEFAULT_CURRENCY, Locale } from '@headless-workspace/config';
import { ArrayHelpers, ValueHelpers } from '@headless-workspace/utils';
import { FlagValue, mapFlags, mapMedia, MediaValue, ProductType } from '../../../client';
import {
    CapacityFormat,
    LoyaltyConfigsValue,
    mapProductBrandData,
    mapProductPriceData,
    PaymentMethodsValue,
    PriceDTO,
    ProductBrandData,
    ProductPriceData,
    PromotionType,
    VipBrandsValue,
} from '../../../server';
import { ProductDetailDTO } from './ProductDetailDTO';
import { mapProductDetailSetItem, ProductSetItemValue } from './ProductDetailSetValue';
import { extractVariantFormats, mapProductDetailVariant, ProductDetailVariantValue } from './ProductDetailVariantValue';
import { mapProductInfo, ProductInfoTabParamsValue } from './ProductInfoTabParamsValue';
import { mapProductLoyalty, ProductLoyaltyInfoValue } from './ProductLoyaltyInfoValue';
import { mapProductDetailBanners, ProductDetailBannersValue } from './banner';
import { BreadcrumbValue, mapBreadcrumbValue } from './breadcrumb';

type BaseProductDetailValue = Omit<ProductPriceData, 'priceBeforeDiscount'> &
    ProductBrandData & {
        name?: {
            full: string;
            partOne: string;
            partTwo?: string;
        };
        description: string;
        vat?: number;
        rating: number;
        totalIndividualPrice?: number;
        reviewCount: number;
        stockLevel: number;
        orderable?: boolean;
        medias: MediaValue[];
        flags?: FlagValue[];
        breadcrumb: BreadcrumbValue;
        productInfoTabParams: ProductInfoTabParamsValue[];
        pricePerUnit?: string;
        socialProof?: string;
        buyNowPayLaterContent: PaymentMethodsValue;
        promotion?: PromotionType;
        banners?: ProductDetailBannersValue;
        isRedDot: boolean;
        url?: string;
        hasAudio?: boolean;
        isEngravable: boolean;
        variants: ProductDetailVariantValue[];
        formats: CapacityFormat[];
        range?: string;
        axis?: string;
        nature?: string;
        loyalty?: ProductLoyaltyInfoValue;
        metadata?: {
            title?: string;
            description?: string;
        };
        isBundle: boolean;
    };

export type ProductSetDetailValue = BaseProductDetailValue & {
    type: ProductType.PRODUCT_SET;
    productSetId: string;
    productsSetList: ProductSetItemValue[]; // Mandatory when type is PRODUCT_SET
};

export type ProductStandardDetailValue = BaseProductDetailValue & {
    type: Exclude<ProductType, ProductType.PRODUCT_SET>;
    sku: string;
    masterId: string;
    productsSetList?: never;
};

export type ProductDetailValue = ProductSetDetailValue | ProductStandardDetailValue;

const isProductNameOnTwoLines = (brandId?: string): boolean => {
    if (!brandId) {
        return false;
    }

    return BrandConfig.productNameOnTwoLines.includes(brandId);
};

export const getPromotionKey = (
    promotion: ProductDetailDTO['promotion'],
    prices: PriceDTO,
): PromotionType | undefined => {
    const { price, priorPrice, originalPrice } = prices;

    switch (promotion) {
        case 'OMNIBUS':
            return PromotionType.OMNIBUS_NEGATIVE; // --> OMNIBUS negative promotion
        case 'STANDARD':
            return PromotionType.STANDARD; // --> Standard promotion
        case 'REGULAR':
            return PromotionType.REGULAR; // --> Without promotion
        case undefined: {
            // Rules for OMNIBUS Positive
            if (price && priorPrice && originalPrice && price >= priorPrice) {
                return PromotionType.OMNIBUS_POSITIVE; // --> OMNIBUS positive promotion
            }
            // Rules for no variation
            if (price && originalPrice && price === originalPrice) {
                return PromotionType.REGULAR; // --> Without promotion
            }
            return undefined;
        }
    }
};

export const mapProductDetail = (
    dto: ProductDetailDTO,
    vipBrands: VipBrandsValue,
    paymentMethods: PaymentMethodsValue,
    locale: Locale,
    loyaltyConfig?: LoyaltyConfigsValue,
): ProductDetailValue => {
    const [namePartOne, ...namePartTwo] = dto.name
        ? isProductNameOnTwoLines(dto.brand.id)
            ? dto.name.split(' - ')
            : [dto.name]
        : [];

    const currency = dto.currency ?? DEFAULT_CURRENCY;
    const prices = mapProductPriceData(dto, currency);
    let variants: ProductDetailVariantValue[] = [];
    const type = dto.type ? ProductType[dto.type] : ProductType.MONO_SKU;

    if (dto.type !== ProductType.PRODUCT_SET) {
        variants = dto.variants.map((variant) => mapProductDetailVariant(variant, currency));
    }

    const product = {
        isBundle: !!dto.kit && dto.type === ProductType.MONO_SKU,
        name: dto.name
            ? {
                  full: dto.name,
                  partOne: namePartOne,
                  partTwo: ArrayHelpers.isNotEmpty(namePartTwo) ? namePartTwo.join(' - ') : undefined,
              }
            : undefined,
        url: ValueHelpers.mapOptional(dto.url),
        productInfoTabParams: mapProductInfo(dto),
        description: dto.description ?? '',
        vat: ValueHelpers.mapOptional(dto.vat),
        totalIndividualPrice: dto.valuePrice ?? undefined,
        rating: dto.rating ?? 0,
        reviewCount: dto.reviewCount ?? 0,
        stockLevel: dto.stockLevel ?? 0,
        orderable: ValueHelpers.mapOptional(dto.orderable),
        medias: dto.medias.map(mapMedia),
        flags: mapFlags(dto.flags, dto.brand.id ?? '', vipBrands),
        ...mapProductBrandData(dto, vipBrands),
        ...prices,
        breadcrumb: mapBreadcrumbValue(dto.breadcrumbs),
        pricePerUnit: ValueHelpers.mapOptional(dto.pricePerUnit),
        socialProof:
            vipBrands[dto.brand.id ?? '']?.isNotSocialProof === true
                ? undefined
                : ValueHelpers.mapOptional(dto.socialProof),
        buyNowPayLaterContent: paymentMethods,
        promotion: getPromotionKey(dto.promotion, prices),
        type,
        banners: mapProductDetailBanners(dto.banners), // FIXME: to be adjusted when unmock
        isRedDot: dto.isRedDot ?? false,
        hasAudio: dto.hasAudio,
        isEngravable: dto.isEngravable,
        formats: extractVariantFormats(variants),
        loyalty: mapProductLoyalty(ValueHelpers.mapOptional(dto.loyaltyPointsToEarn), locale, loyaltyConfig),
        variants,
        axis: ValueHelpers.mapOptional(dto.axis),
        range: ValueHelpers.mapOptional(dto.range),
        nature: ValueHelpers.mapOptional(dto.nature),
        metadata:
            dto.metadata && (dto.metadata.pageTitle || dto.metadata.pageDescription)
                ? {
                      title: ValueHelpers.mapOptional(dto.metadata.pageTitle),
                      description: ValueHelpers.mapOptional(dto.metadata.pageDescription),
                  }
                : undefined,
    };

    if (dto.type === ProductType.PRODUCT_SET) {
        return {
            ...product,
            productSetId: dto.id,
            type: ProductType.PRODUCT_SET,
            productsSetList: dto.products.map(mapProductDetailSetItem(currency)),
        };
    }

    return {
        ...product,
        sku: dto.defaultVariantId ?? dto.id,
        // The case where the masterId is null is impossible at this level, fallback to empty string to avoid nullable impossible case
        masterId: dto.masterId ?? '',
        type: dto.type ? ProductType[dto.type] : ProductType.MONO_SKU,
    };
};
